import React from 'react';
import {
  TextField as MuiTextField,
  Menu,
  MenuItem as MuiMenuItem,
  InputAdornment,
  Divider,
  Input,
  LinearProgress,
  Tooltip
} from '@material-ui/core';
import MuiDrawer from '@material-ui/core/Drawer';
import Checkbox from '@material-ui/core/Checkbox';
import { ArrowDropDown, Search } from '@material-ui/icons';
import { FixedSizeList } from 'react-window';

import InputLabel from '../InputLabel';
import styled from 'styled-components/macro';
import Colors from '../../internal-components/Colors';
import useGlobalVariables from '../../hooks/useGlobalVariables';
import Animation from '../Animation';

export const DisplayInput = styled(Input)`
  && {
    cursor: pointer;
    padding: 0.1em 0.8em;
    margin: 0.4em 0;
    ${({ fullWidth }) => !fullWidth && 'max-width: 35em;'}
    ${({ disabled }) => disabled && 'pointer-events: none; opacity:0.7;'}
    ${({ $variant }) => {
      switch ($variant) {
        case 'contained':
          return 'background: #f8f9fa; border-radius: 10px;';
        case 'inline':
          return 'padding: 0;';
        case 'ot_c':
          // end-user form view (for drop down fields)
          return 'background: #fff; border: 1px solid #c4c4c4; border-radius: 6px; height: 37px;';
        case 'mobile':
          return (
            'width: 100%; background: #fff; padding: 0.6em 1.2em; margin: 0; border-bottom: 1px solid ' +
            Colors.OT_GREY +
            ';'
          );
        case 'form-builder':
          return 'width: 100%; background: #fff; border: 1px solid #c4c4c4; border-radius: 6px; &:hover {border: 1px solid #000;}';
        case 'outlined':
        default:
          return 'background: #fff; border: 1px solid #c4c4c4; border-radius: 6px; &:hover {border: 1px solid #000;}';
      }
    }}
    transition: 0.2s;
  }
  input {
    cursor: pointer;
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 14px;
  }

  ${({ $customCSS }) => $customCSS}
`;

const DropDownMenu = styled(Menu)`
  [class*='MuiPopover-paper'] {
    ${({ $variant }) => {
      switch ($variant) {
        case 'contained':
          return 'border-radius: 10px;';

        default:
          break;
      }
    }}
  }
`;

const Drawer = styled(MuiDrawer)`
  && {
    z-index: 999999 !important;
  }

  [class*='MuiDrawer-paperAnchorBottom'] {
    height: 50vh;
  }
`;

const MenuItem = styled(MuiMenuItem)`
  && {
    ${({ fullWidth }) => !fullWidth && 'max-width: 40em;'}
    white-space: normal;
    ${({ selected }) =>
      selected &&
      'background: rgb(76, 132, 255) !important; text-shadow: none !important;'}
  }

  &:hover {
    background: ${({ selected }) =>
      selected ? 'rgb(45 86 175) ' : 'rgba(76, 132, 255, 0.1) '}!important;
    transition: 0.25s;
  }

  ${({ $customCSS }) => $customCSS}
`;

const SearchField = styled(MuiTextField)`
  && {
    padding: 0 1em;
  }
`;

const SearchIcon = styled(Search)`
  && {
    opacity: 0.4;
  }
`;

const WhiteCheckBox = styled(Checkbox)`
  && {
    ${({ checked }) => checked && 'color: white !important;'}
  }
`;

export const MenuH3 = styled.h3`
  margin: 16px;
  color: #000;
  outline: none !important;
`;

const MobileSearchContainer = styled.div`
  background: #fff;
  width: 100%;
  padding: 0.8rem 0;
  position: sticky;
  top: 0;
  z-index: 999999999999;
`;

const SelectView = props => {
  const { t } = props;
  const { isMobileView } = useGlobalVariables();
  const optionsProps = {
    options: props.options,
    tanslateValue: props.tanslateValue,
    isSelected: props.isSelected,
    onSelected: props.onSelected,
    openMenuOptionParser: props.openMenuOptionParser,
    t,
    virtualiziztionProps: props.virtualiziztionProps,
    useMultiSelect: props.useMultiSelect,
    fullWidth: props.inputProps.fullWidth
  };

  return (
    <>
      {props.label && (
        <InputLabel id={`label-${props.label}`} content={props.label} />
      )}
      <Tooltip title={props.previewTootltipText} arrow={props.tooltipArrow}>
        <DisplayInput
          disabled={props.disabled}
          type={props.variant === 'inline' ? 'button' : 'text'}
          {...props.inputProps}
          customCSS={undefined}
          $customCSS={props.inputProps ? props.inputProps.customCSS : ''}
          readOnly
          value={
            props.forceDisplayValue
              ? props.forceDisplayValue
              : props.selected &&
                Object.keys(props.selected).length &&
                props.getDisplayValue().length
              ? props.getDisplayValue()
              : t(props.placeholder)
          }
          onClick={e => props.setAnchorEl(e.currentTarget)}
          endAdornment={
            props.noArrow ? null : (
              <InputAdornment position="end">
                <ArrowDropDown />
              </InputAdornment>
            )
          }
          $variant={props.variant}
          disableUnderline={
            props.inputProps.disableUnderline || props.variant !== 'inline'
          }
        />
      </Tooltip>
      {props.variant === 'mobile' || isMobileView ? (
        <>
          <Drawer
            anchor="bottom"
            open={Boolean(props.anchorEl)}
            onClose={() => props.setAnchorEl(null)}
          >
            <>
              <MobileSearchContainer>
                {props.searchable && (
                  <SearchField
                    inputRef={ref => props.setSearchInputEl(ref)}
                    onKeyDown={e => e.stopPropagation()}
                    fullWidth
                    onBlur={e => {
                      if (window.event.type === 'input') {
                        e.target.focus();
                      }
                    }}
                    placeholder={
                      props.isUserLookup
                        ? 'Search by email for best results'
                        : 'Search...'
                    }
                    value={props.filterText}
                    onChange={e => props.setFilterText(e.target.value)}
                    InputProps={{
                      disableUnderline: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              </MobileSearchContainer>
              {props.virtualize ? (
                <VirtulizedList {...optionsProps} />
              ) : (
                <List {...optionsProps} />
              )}
            </>
          </Drawer>
        </>
      ) : (
        <DropDownMenu
          // MenuListProps={{ disableListWrap: true }}
          {...props.menuProps}
          $variant={props.variant}
          // keepMounted
          anchorEl={props.anchorEl}
          // disablePortal
          onClose={() => props.setAnchorEl(null)}
          onEntered={() => props.searchInputEl && props.searchInputEl.focus()}
          open={Boolean(props.anchorEl)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
          getContentAnchorEl={null}
          PaperProps={{
            style: { minWidth: props.menuWidth, maxHeight: 500 }
          }}
          ref={props.selectRef}
        >
          {/* <Paper> */}
          {props.searchable && (
            <div>
              <SearchField
                inputRef={ref => props.setSearchInputEl(ref)}
                onKeyDown={e => e.stopPropagation()}
                fullWidth
                onBlur={e => {
                  if (window.event.type === 'input') {
                    e.target.focus();
                  }
                }}
                placeholder={
                  props.isUserLookup
                    ? 'Search by email for best results'
                    : 'Search...'
                }
                value={props.filterText}
                onChange={e => props.setFilterText(e.target.value)}
                InputProps={{
                  disableUnderline: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
              <Divider />
            </div>
          )}
          {props.isLoading ? (
            <LinearProgress />
          ) : props.virtualize ? (
            <VirtulizedList {...optionsProps} />
          ) : (
            <List {...optionsProps} />
          )}
        </DropDownMenu>
      )}
    </>
  );
};

// Simple select component options
const List = ({
  options,
  tanslateValue,
  isSelected,
  onSelected,
  openMenuOptionParser,
  t,
  useMultiSelect,
  ...props
}) => {
  return options.map(opt => {
    if (opt.isSectionBreak) {
      return <MenuH3 tabIndex={null}>{opt.text}</MenuH3>;
    } else {
      return (
        <MenuItem
          {...opt}
          fullWidth={props.fullWidth}
          key={tanslateValue(opt.value)}
          selected={isSelected(opt.value)}
          onClick={e => onSelected(opt.value, e)}
          style={{ marginLeft: opt.isSubField ? '2rem' : null }}
        >
          {useMultiSelect && <WhiteCheckBox checked={isSelected(opt.value)} />}

          {opt && opt.value === -1
            ? opt.text
            : typeof openMenuOptionParser(opt) === 'string'
            ? t(openMenuOptionParser(opt))
            : openMenuOptionParser(opt)}
        </MenuItem>
      );
    }
  });
};

// Virtualized select component options
const VirtulizedList = ({ virtualiziztionProps, options, ...listProps }) => {
  // TODO: I think translations isn't working - need to verify

  const height =
    virtualiziztionProps.itemSize * options.length < 400
      ? virtualiziztionProps.itemSize * options.length
      : 400;

  return (
    <FixedSizeList
      height={height}
      itemCount={options.length}
      itemSize={virtualiziztionProps.itemSize}
      width={virtualiziztionProps.width}
      itemData={{ options, ...listProps }}
    >
      {VirtulizedMenuItem}
    </FixedSizeList>
  );
};

const VirtulizedMenuItem = ({ index, data, style, ...props }) => {
  const opt = data.options[index];
  const fullStyle = style || {};
  if (opt.isSubField) {
    fullStyle.marginLeft = '2rem';
  }
  return (
    <MenuItem
      {...opt}
      fullWidth={props.fullWidth}
      // key={props.tanslateValue(opt.value)}
      selected={data.isSelected(opt.value)}
      onClick={() => data.onSelected(opt.value)}
      style={fullStyle}
    >
      {opt && opt.value === -1
        ? opt.text
        : typeof data.openMenuOptionParser(opt) === 'string'
        ? data.t(data.openMenuOptionParser(opt))
        : data.openMenuOptionParser(opt)}
    </MenuItem>
  );
};

export default SelectView;
